// Tailwind classes removed. Add your CSS rules in a separate stylesheet.

import React, { useState, useEffect } from 'react';
import { Bell, AlertTriangle, AlertCircle, Info, X, Edit, ChevronDown, ChevronRight } from 'lucide-react';
import testAccountsData from './test_accounts.json';
import styles from './accountMonitors.module.css';

export function ChartOfAccountsMonitor() {
  const [accountsData, setAccountsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monitors, setMonitors] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAccountForMonitor, setCurrentAccountForMonitor] = useState(null);
  const [activeView] = useState('chart-of-accounts');
  const [monitorForm, setMonitorForm] = useState({
    comparisonType: 'absolute',
    comparisonOperator: '>',
    comparisonValue: 0,
    alertType: 'warning',
  });
  const [hoveredAccount, setHoveredAccount] = useState(null);
  const [editingMonitor, setEditingMonitor] = useState(null);
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [expandedTypes, setExpandedTypes] = useState({});

  useEffect(() => {
    const loadData = async () => {
      try {
        // Import the test data JSON directly
        const data = testAccountsData;
        
        // Add previous month data for demo purposes
        data.accounts = data.accounts.map(account => {
          // Generate a previous month value that's somewhat different from the current value
          // Generate a previous month value that's somewhat different from the current value
          
          // Create a variation between -15% and +15% for the previous month
          const variationFactor = 0.85 + (Math.random() * 0.3); // Between 0.85 and 1.15
            
          return {
            ...account,
            previousMonthval: account.val !== null 
              ? account.val * variationFactor 
              : null,
            previousMonthp_l: account.p_l !== null 
              ? account.p_l * variationFactor 
              : null,
            // Apply the same logic to sub
            sub: account.sub 
              ? account.sub.map(subAcc => {
                  
                  const subVariationFactor = 0.85 + (Math.random() * 0.3);
                  
                  return {
                    ...subAcc,
                    previousMonthval: subAcc.val !== null 
                      ? subAcc.val * subVariationFactor 
                      : null,
                    previousMonthp_l: subAcc.p_l !== null 
                      ? subAcc.p_l * subVariationFactor 
                      : null
                  };
                })
              : []
          };
        });
        
        // Add previous month data for summary metrics
        data.previousMonthRevenue = data.revenue * (0.85 + (Math.random() * 0.3));
        data.previousMonthExpenses = data.expenses * (0.85 + (Math.random() * 0.3));
        data.previousMonthNetIncome = data.previousMonthRevenue - data.previousMonthExpenses;
        data.previousMonthCashOnHand = data.cashOnHand * (0.85 + (Math.random() * 0.3));
        
        // Set up some example monitors with pre-set triggered status for demo
        const demoMonitors = [
          // Monitor for Accounts Payable - error if over $40,000
          {
            id: 'demo-monitor-1',
            accountId: '33',
            name: 'Accounts Payable (A/P)',
            comparisonType: 'absolute',
            comparisonOperator: '>',
            comparisonValue: 40000,
            alertType: 'error',
            active: true,
            triggered: true
          },
          // Monitor for Accounts Receivable - warning if over $25,000
          {
            id: 'demo-monitor-2',
            accountId: '84',
            name: 'Accounts Receivable (A/R)',
            comparisonType: 'absolute',
            comparisonOperator: '>',
            comparisonValue: 25000,
            alertType: 'warning',
            active: true,
            triggered: true
          },
          // Monitor for Landscaping Services - info about revenue targets
          {
            id: 'demo-monitor-3',
            accountId: '45',
            name: 'Landscaping Services',
            comparisonType: 'absolute',
            comparisonOperator: '<',
            comparisonValue: 20000,
            alertType: 'info',
            active: true,
            triggered: true
          },
          // Monitor for Checking - warning if below $20,000
          {
            id: 'demo-monitor-4',
            accountId: '35',
            name: 'Checking',
            comparisonType: 'absolute',
            comparisonOperator: '<',
            comparisonValue: 20000,
            alertType: 'warning',
            active: true,
            triggered: true
          },
          // Monitor for Notes Payable - not triggered for demonstration
          {
            id: 'demo-monitor-5',
            accountId: '44',
            name: 'Notes Payable',
            comparisonType: 'absolute',
            comparisonOperator: '>',
            comparisonValue: 30000,
            alertType: 'error',
            active: true,
            triggered: false
          }
        ];

        // Expand all types and accounts that have monitors for the demo
        const typesToExpand = {};
        const accountsToExpand = {};
        
        // Group accounts by type
        const accountsByType = {};
        data.accounts.forEach(acc => {
          if (!accountsByType[acc.type]) {
            accountsByType[acc.type] = [];
          }
          
          // Only add top-level accounts (those without a parent)
          if (acc.parent === null) {
            accountsByType[acc.type].push(acc);
          }
          
          // Expand accounts that have monitors
          const hasMonitor = demoMonitors.some(m => m.accountId === acc.accountId);
          if (hasMonitor) {
            typesToExpand[acc.type] = true;
            if (acc.sub && acc.sub.length > 0) {
              accountsToExpand[acc.accountId] = true;
            }
          }
        });
        
        setAccountsData(data);
        setMonitors(demoMonitors);
        setExpandedTypes(typesToExpand);
        setExpandedAccounts(accountsToExpand);
        setIsLoading(false);
      } catch (err) {
        setError('Error loading chart of accounts data');
        setIsLoading(false);
        console.error('Error loading data:', err);
      }
    };

    loadData();
  }, []);

  // Format currency values
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '$0.00';
    const numValue = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(numValue);
  };
  
  // Format percentage change
  const formatPercentChange = (current, previous) => {
    if (previous === 0 || previous === null || previous === undefined) {
      return current > 0 ? "+∞%" : current < 0 ? "-∞%" : "0%";
    }
    
    const percentChange = ((current - previous) / Math.abs(previous)) * 100;
    const prefix = percentChange > 0 ? "+" : "";
    return `${prefix}${percentChange.toFixed(1)}%`;
  };
  
  // Determine color class based on percentage change
  const getPercentChangeColorClass = (current, previous) => {
    if (previous === 0 || previous === null || previous === undefined) return "";
    const change = current - previous;
    return change > 0 ? styles.textGreen600 : change < 0 ? styles.textRed600 : "";
  };

  // Safe check if a monitor exists and is triggered for an account
  const getMonitorStatus = (accountId, name) => {
    if (!monitors || !monitors.length) return { triggered: false, monitorExists: false };
    
    const accountMonitor = monitors.find(m => 
      (m.accountId === accountId || m.name === name) && m.active
    );
    
    if (!accountMonitor) return { triggered: false, monitorExists: false };
    
    return { 
      triggered: accountMonitor.triggered, 
      alertType: accountMonitor.alertType, 
      monitorExists: true 
    };
  };

  const handleCreateMonitor = () => {
    if (!currentAccountForMonitor) return;
    
    const { accountId, name } = currentAccountForMonitor;
    
    // Check if editing existing monitor
    if (editingMonitor) {
      setMonitors(prev => prev.map(m => 
        m.id === editingMonitor.id 
          ? { 
              ...editingMonitor,
              comparisonType: monitorForm.comparisonType,
              comparisonOperator: monitorForm.comparisonOperator,
              comparisonValue: parseFloat(monitorForm.comparisonValue),
              alertType: monitorForm.alertType,
            } 
          : m
      ));
    } else {
      // Create new monitor
      const newMonitor = {
        id: `monitor-${Date.now()}`,
        accountId,
        name,
        comparisonType: monitorForm.comparisonType,
        comparisonOperator: monitorForm.comparisonOperator,
        comparisonValue: parseFloat(monitorForm.comparisonValue),
        alertType: monitorForm.alertType,
        active: true,
        triggered: false
      };
      
      setMonitors(prev => [...prev, newMonitor]);
    }
    
    // Reset and close
    setIsModalOpen(false);
    setCurrentAccountForMonitor(null);
    setEditingMonitor(null);
    setMonitorForm({
      comparisonType: 'absolute',
      comparisonOperator: '>',
      comparisonValue: 0,
      alertType: 'warning',
    });
  };

  const openMonitorModal = (accountId, name, amount) => {
    // Check if monitor already exists for this account
    const existingMonitor = monitors.find(m => m.accountId === accountId);
    
    if (existingMonitor) {
      setEditingMonitor(existingMonitor);
      setMonitorForm({
        comparisonType: existingMonitor.comparisonType,
        comparisonOperator: existingMonitor.comparisonOperator,
        comparisonValue: existingMonitor.comparisonValue,
        alertType: existingMonitor.alertType,
      });
    } else {
      setEditingMonitor(null);
    }
    
    setCurrentAccountForMonitor({ accountId, name, amount });
    setIsModalOpen(true);
  };

  const deleteMonitor = (monitorId) => {
    setMonitors(prev => prev.filter(m => m.id !== monitorId));
    setIsModalOpen(false);
    setEditingMonitor(null);
    setCurrentAccountForMonitor(null);
  };

  const toggleAccountExpand = (accountId) => {
    setExpandedAccounts(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };

  const toggleTypeExpand = (type) => {
    setExpandedTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Render a single account row
  const renderAccountRow = (account, depth = 0) => {
    const { 
      accountId, 
      name, 
      type, 
      val, 
      p_l, 
      previousMonthval, 
      previousMonthp_l, 
      sub 
    } = account;
    
    // Choose which amount to display based on account type and active view
    const isIncomeOrExpense = type === 'Income' || type === 'Expense' || type === 'Cost of Goods Sold';
    const usesPLAmounts = isIncomeOrExpense || activeView === 'profit-and-loss';
    
    const displayAmount = usesPLAmounts ? p_l : val;
    const previousAmount = usesPLAmounts ? previousMonthp_l : previousMonthval;
    
    // For P&L view, filter out sub-accounts with zero P&L amounts
    let relevantsub = sub;
    // if (activeView === 'profit-and-loss' && sub) {
    //   relevantsub = sub.filter(subAcc => {
    //     return isRelevantForPL(subAcc.type) && subAcc.p_l !== 0;
    //   });
      
    //   // If there are no relevant sub-accounts in P&L view and this account also has zero P&L amount, skip it
    //   if (relevantsub.length === 0 && p_l === 0) {
    //     return null;
    //   }
    // }
    
    // Check if there's an active monitor for this account
    const monitorStatus = getMonitorStatus(accountId, name);
    
    // Set row background color based on alert type
    let rowColorClass = '';
    let iconComponent = null;
    // Only one icon per row, always rendered in the same spot
    if (monitorStatus.triggered) {
      switch (monitorStatus.alertType) {
        case 'info':
          rowColorClass = styles.bgBlue50;
          iconComponent = <Info size={16} className={styles.textBlue500} />;
          break;
        case 'warning':
          rowColorClass = styles.bgYellow50;
          iconComponent = <AlertTriangle size={16} className={styles.textYellow500} />;
          break;
        case 'error':
          rowColorClass = styles.bgRed50;
          iconComponent = <AlertCircle size={16} className={styles.textRed500} />;
          break;
        default:
          break;
      }
    } else if (monitorStatus.monitorExists) {
      iconComponent = <Bell size={16} className={styles.textGray400} />;
    }
    
    const hassub = sub && sub.length > 0;
    const isExpanded = expandedAccounts[accountId];
    const percentChangeClass = getPercentChangeColorClass(displayAmount, previousAmount);
    
    return (
      <React.Fragment key={accountId}>
        <tr 
          className={`${styles.accountRow}${rowColorClass ? ' ' + rowColorClass : ''}${hassub ? ' ' + styles.hasSub : ''}`}
          onMouseEnter={() => setHoveredAccount(accountId)}
          onMouseLeave={() => setHoveredAccount(null)}
        >
          <td className={styles.accountCell}>
            <div className={styles.accountCellInner} style={{ paddingLeft: `${depth * 20}px` }}>
              {hassub && (
                <span 
                  className={styles.expandToggle}
                  onClick={() => toggleAccountExpand(accountId)}
                >
                  {isExpanded ? 
                    <ChevronDown size={16} className={styles.chevronIcon} /> : 
                    <ChevronRight size={16} className={styles.chevronIcon} />
                  }
                </span>
              )}
              {!hassub && <span className={styles.expandPlaceholder}></span>}
              {/* Always render icon container with fixed width for alignment */}
              <span className={styles.alertIcon}>
                {iconComponent}
              </span>
              <span 
                className={styles.accountName}
                onClick={() => hassub && toggleAccountExpand(accountId)}
              >
                {name}
              </span>
            </div>
          </td>
          <td className={styles.accountType}>{type}</td>
          <td className={styles.accountAmount}>{formatCurrency(displayAmount)}</td>
          <td className={styles.accountAmount}>{formatCurrency(previousAmount)}</td>
          <td className={`${styles.accountChange}${percentChangeClass ? ' ' + percentChangeClass : ''}`}>{formatPercentChange(displayAmount, previousAmount)}</td>
          <td className={styles.accountMonitorBtn}>
            {(hoveredAccount === accountId || monitorStatus.monitorExists) && (
              <button 
                onClick={() => openMonitorModal(accountId, name, displayAmount)}
                className={styles.monitorBtnMon}
                title={monitorStatus.monitorExists ? "Edit monitor" : "Create monitor"}
              >
                {monitorStatus.monitorExists ? 
                  <Edit size={14} className={styles.editIconMon} /> :
                  <Bell size={14} className={styles.bellIconMon} />
                }
              </button>
            )}
          </td>
        </tr>
        
        {/* Render sub-accounts if expanded */}
        {hassub && isExpanded && (
          activeView === 'profit-and-loss' 
            ? relevantsub.map(subAccount => renderAccountRow(subAccount, depth + 1))
            : sub.map(subAccount => renderAccountRow(subAccount, depth + 1))
        )}
      </React.Fragment>
    );
  };

  // Group accounts by type and render
  const renderAccountsByType = () => {
    if (!accountsData || !accountsData.accounts) return null;
    
    // Group accounts by type
    const accountsByType = {};
    accountsData.accounts.forEach(acc => {
      // Filter accounts by type if we're in P&L view
    //   if (activeView === 'profit-and-loss' && !isRelevantForPL(acc.type)) {
    //     return;
    //   }
      
      if (!accountsByType[acc.type]) {
        accountsByType[acc.type] = [];
      }
      
      // Only add top-level accounts (those without a parent)
      if (acc.parent === null) {
        // In P&L view, only include accounts with non-zero P&L amounts or with sub-accounts that have non-zero P&L amounts
        // if (activeView === 'profit-and-loss') {
        //   const hasNonZeroPLAmount = acc.p_l !== 0;
        //   const hasRelevantsub = acc.sub && acc.sub.some(
        //     sub => sub.p_l !== 0 && isRelevantForPL(sub.type)
        //   );
          
        //   if (!hasNonZeroPLAmount && !hasRelevantsub) {
        //     return;
        //   }
        // }
        
        accountsByType[acc.type].push(acc);
      }
    });
    
    // Order account types in a logical financial statement order
    const typeOrder = [
      'Bank', 
      'Accounts Receivable', 
      'Other Current Asset',
      'Fixed Asset',
      'Accounts Payable',
      'Credit Card',
      'Other Current Liability',
      'Long Term Liability',
      'Equity',
      'Income',
      'Cost of Goods Sold',
      'Expense',
      'Other Income',
      'Other Expense'
    ];
    
    return typeOrder.map(type => {
      if (!accountsByType[type]) return null;
      
      const isExpanded = expandedTypes[type];
      
      return (
        <React.Fragment key={type}>
          <tr 
            className={`${styles.bgGray100} ${styles.cursorPointer}`}
            onClick={() => toggleTypeExpand(type)}
          >
            <td colSpan={6} className={`${styles.p2} ${styles.fontBold}`}>
              <div className={styles.flex + ' ' + styles.itemsCenter}>
                <span className={styles.mr2}>
                  {isExpanded ? 
                    <ChevronDown size={16} /> : 
                    <ChevronRight size={16} />
                  }
                </span>
                {type}
              </div>
            </td>
          </tr>
          
          {/* Render accounts of this type if expanded */}
          {isExpanded && accountsByType[type].map(account => 
            renderAccountRow(account)
          )}
        </React.Fragment>
      );
    });
  };

  // Monitor Modal
  const renderMonitorModal = () => {
    if (!isModalOpen || !currentAccountForMonitor) return null;
    
    const { name, amount } = currentAccountForMonitor;
    const isEditing = !!editingMonitor;
    
    return (
      <div className={`${styles.fixed} ${styles.inset0} ${styles.bgBlack} ${styles.bgOpacity50} ${styles.flex} ${styles.itemsCenter} ${styles.justifyCenter} ${styles.z50}`}>
        <div className={`${styles.bgWhite} ${styles.roundedLg} ${styles.p6} ${styles.maxWMd} ${styles.wFull}`}>
          <div className={`${styles.flex} ${styles.justifyBetween} ${styles.itemsCenter} ${styles.mb4}`}>
            <h2 className={`${styles.textXl} ${styles.fontBold}`}>
              {isEditing ? 'Edit Monitor' : 'Create Monitor'}
            </h2>
            <button 
              onClick={() => {
                setIsModalOpen(false);
                setEditingMonitor(null);
              }}
              className={`${styles.p1} ${styles.rounded} hover:bg-gray-200`}
            >
              <X size={20} />
            </button>
          </div>
          
          <div className={styles.mb4}>
            <div className={styles.fontMedium}>Account:</div>
            <div>{name}</div>
          </div>
          
          <div className={styles.mb4}>
            <div className={styles.fontMedium}>Current Value:</div>
            <div>{formatCurrency(amount)}</div>
          </div>
          
          <div className={styles.mb4}>
            <label className={`block ${styles.fontMedium} ${styles.mb1}`}>Comparison Type</label>
            <select 
              className={`${styles.wFull} ${styles.p2} border ${styles.rounded}`}
              value={monitorForm.comparisonType}
              onChange={(e) => setMonitorForm({...monitorForm, comparisonType: e.target.value})}
            >
              <option value="absolute">Absolute Value</option>
              <option value="percentage">Percentage Change</option>
              
            </select>
          </div>
          
          <div className={`${styles.mb4} ${styles.flex} ${styles.gap4}`}>
            <div className="flex-1">
              <label className={`block ${styles.fontMedium} ${styles.mb1}`}>Operator</label>
              <select 
                className={`${styles.wFull} ${styles.p2} border ${styles.rounded}`}
                value={monitorForm.comparisonOperator}
                onChange={(e) => setMonitorForm({...monitorForm, comparisonOperator: e.target.value})}
              >
                <option value=">">Greater than (&gt;)</option>
                <option value="<">Less than (&lt;)</option>
                <option value="==">Equal to (=)</option>
                <option value=">=">Greater than or equal to (≥)</option>
                <option value="<=">Less than or equal to (≤)</option>
              </select>
            </div>
            
            <div className="flex-1">
              <label className={`block ${styles.fontMedium} ${styles.mb1}`}>Value</label>
              <input 
                type="number" 
                className={`${styles.wFull} ${styles.p2} border ${styles.rounded}`}
                value={monitorForm.comparisonValue}
                onChange={(e) => setMonitorForm({...monitorForm, comparisonValue: e.target.value})}
                step="1"
              />
            </div>
          </div>
          
          <div className={styles.mb6}>
            <label className={`block ${styles.fontMedium} ${styles.mb1}`}>Alert Type</label>
            <select 
              className={`${styles.wFull} ${styles.p2} border ${styles.rounded}`}
              value={monitorForm.alertType}
              onChange={(e) => setMonitorForm({...monitorForm, alertType: e.target.value})}
            >
              <option value="info">Information (Blue)</option>
              <option value="warning">Warning (Yellow)</option>
              <option value="error">Error (Red)</option>
            </select>
          </div>
          
          <div className={styles.flex + ' ' + styles.justifyBetween}>
            {isEditing && (
              <button
                onClick={() => deleteMonitor(editingMonitor.id)}
                className={`px-4 py-2 ${styles.bgRed500} text-white ${styles.rounded} hover:bg-red-600`}
              >
                Delete Monitor
              </button>
            )}
            <div className={isEditing ? '' : styles.mlAuto}>
              <button
                onClick={handleCreateMonitor}
                className={`px-4 py-2 ${styles.bgBlue500} text-white ${styles.rounded} hover:bg-blue-600`}
              >
                {isEditing ? 'Update Monitor' : 'Create Monitor'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <div className={`${styles.p4} ${styles.textLg}`}>Loading chart of accounts data...</div>;
  }

  if (error || !accountsData) {
    return <div className={`${styles.p4} ${styles.textLg}`}>Error: {error || 'No data available'}</div>;
  }

  return (
    <div className={styles.accountMonitorContainer}>
      
      <div className={styles.mb6}>
        <div className={styles.textCenter}>
          <div className={`${styles.flex} ${styles.justifyCenter} ${styles.mb4}`}>
            {/* <div className="inline-flex rounded-md shadow-sm" role="group">
              <button 
                type="button" 
                className={`px-4 py-2 text-sm font-medium border border-gray-200 rounded-l-lg ${activeView === 'chart-of-accounts' ? 'bg-blue-500 text-white' : 'bg-white text-gray-900 hover:bg-gray-100'}`}
                onClick={() => setActiveView('chart-of-accounts')}
              >
                Chart of Accounts
              </button>
              <button 
                type="button" 
                className={`px-4 py-2 text-sm font-medium border border-gray-200 rounded-r-lg ${activeView === 'profit-and-loss' ? 'bg-blue-500 text-white' : 'bg-white text-gray-900 hover:bg-gray-100'}`}
                onClick={() => setActiveView('profit-and-loss')}
              >
                Profit & Loss
              </button>
            </div> */}
          </div>
          <h1 className={`${styles.text2xl} ${styles.fontBold}`}>{activeView === 'chart-of-accounts' ? 'Chart of Accounts' : 'Profit & Loss Statement'}</h1>
          <div className={`${styles.mt2} ${styles.grid} ${styles.gridCols2} ${styles.mdGridCols4} ${styles.gap6}`}>
            <div className={`${styles.flex} ${styles.flexCol} ${styles.itemsCenter}`}>
              <div className={styles.fontMedium}>Revenue</div>
              <div className={styles.textLg}>{formatCurrency(accountsData.revenue)}</div>
              <div className={`${styles.textSm} ${styles.flex} ${styles.itemsCenter}`}>
                <span className={getPercentChangeColorClass(accountsData.revenue, accountsData.previousMonthRevenue)}>
                  {formatPercentChange(accountsData.revenue, accountsData.previousMonthRevenue)}
                </span>
                <span className={`${styles.textGray500} ${styles.ml2}`}>vs. last month</span>
              </div>
            </div>
            <div className={`${styles.flex} ${styles.flexCol} ${styles.itemsCenter}`}>
              <div className={styles.fontMedium}>Expenses</div>
              <div className={styles.textLg}>{formatCurrency(accountsData.expenses)}</div>
              <div className={`${styles.textSm} ${styles.flex} ${styles.itemsCenter}`}>
                <span className={getPercentChangeColorClass(accountsData.expenses, accountsData.previousMonthExpenses)}>
                  {formatPercentChange(accountsData.expenses, accountsData.previousMonthExpenses)}
                </span>
                <span className={`${styles.textGray500} ${styles.ml2}`}>vs. last month</span>
              </div>
            </div>
            <div className={`${styles.flex} ${styles.flexCol} ${styles.itemsCenter}`}>
              <div className={styles.fontMedium}>Net Income</div>
              <div className={styles.textLg}>{formatCurrency(accountsData.netIncome)}</div>
              <div className={`${styles.textSm} ${styles.flex} ${styles.itemsCenter}`}>
                <span className={getPercentChangeColorClass(accountsData.netIncome, accountsData.previousMonthNetIncome)}>
                  {formatPercentChange(accountsData.netIncome, accountsData.previousMonthNetIncome)}
                </span>
                <span className={`${styles.textGray500} ${styles.ml2}`}>vs. last month</span>
              </div>
            </div>
            <div className={`${styles.flex} ${styles.flexCol} ${styles.itemsCenter}`}>
              <div className={styles.fontMedium}>Cash on Hand</div>
              <div className={styles.textLg}>{formatCurrency(accountsData.cashOnHand)}</div>
              <div className={`${styles.textSm} ${styles.flex} ${styles.itemsCenter}`}>
                <span className={getPercentChangeColorClass(accountsData.cashOnHand, accountsData.previousMonthCashOnHand)}>
                  {formatPercentChange(accountsData.cashOnHand, accountsData.previousMonthCashOnHand)}
                </span>
                <span className={`${styles.textGray500} ${styles.ml2}`}>vs. last month</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* {monitors.length > 0 && (
          <div className="mt-4 p-3 bg-gray-100 rounded-md">
            <h3 className="font-medium mb-2">Active Monitors: {monitors.length}</h3>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center">
                <Bell size={16} className="text-gray-400 mr-1" />
                <span>Monitor Set</span>
              </div>
              <div className="flex items-center">
                <Info size={16} className="text-blue-500 mr-1" />
                <span>Info Alert</span>
              </div>
              <div className="flex items-center">
                <AlertTriangle size={16} className="text-yellow-500 mr-1" />
                <span>Warning Alert</span>
              </div>
              <div className="flex items-center">
                <AlertCircle size={16} className="text-red-500 mr-1" />
                <span>Error Alert</span>
              </div>
            </div>
          </div>
        )} */}
      </div>
      
      <div className={`${styles.overflowAuto} ${styles.shadowMd} ${styles.roundedLg}`}>
        <table className={`${styles.wFull} border-collapse`}>
          <thead>
            <tr className={styles.bgGray200}>
              <th className={`${styles.p2} text-left`}>Account Name</th>
              <th className={`${styles.p2} text-left`}>Type</th>
              <th className={`${styles.p2} text-right`}>Current Month</th>
              <th className={`${styles.p2} text-right`}>Last Month</th>
              <th className={`${styles.p2} text-right`}>Change</th>
              <th className={`w-16 ${styles.p2}`}></th>
            </tr>
          </thead>
          <tbody className={`${styles.divideY} divide-gray-100`}>
            {renderAccountsByType()}
          </tbody>
        </table>
      </div>
      
      {renderMonitorModal()}
    </div>
  );
};
